import { View, StyleSheet, Pressable, ScrollView, Text } from "react-native";
import { useUISlice } from "@/models/useUISlice";
import ModalContainer from "@/ui/ModalContainer";
import { ms } from "@/ui/moderateScale";
import { useTranslation } from "react-i18next";
import { Colors } from "@/constants";
import Icon, { IconType } from "./Icon";
import { AIModel } from "@/models/types";
import { useUserSlice } from "@/models/useUserSlice";
import { useAuth } from "@/models/useAuth";

const AlertWidgetSelectAIModel = () => {
  const visible = useUISlice((state) => state.alertWidget === "selectAIModel");
  const height = useUISlice((state) => state.device.height);
  const setVisible = useUISlice((state) => state.setAlertWidget);
  const width = useUISlice((state) => state.device.width);
  const aiModels = useUserSlice((state) => state.remoteConfig?.aiModels);
  const currentAIModel = useUISlice((state) => state.currentAIModel);
  const isSubscribed = useUserSlice((state) => state.isSubscribed);
  const recentAIModels = useUserSlice((state) => state.recentAIModels);
  const { t } = useTranslation();

  if (!visible) return null;

  const onSelectAIModel = (model: AIModel) => {
    if (!isSubscribed && model.isPremium) {
      useUISlice.getState().setAlertWidget(undefined);
      useUISlice.getState().setPaywallVisible(true);
      return;
    }

    useUserSlice
      .getState()
      .setRecentAIModels(
        [model, ...(recentAIModels || [])]
          .filter((m, i, arr) => arr.findIndex((a) => a.id === m.id) === i)
          .slice(0, 3)
      );
    useUISlice.getState().setCurrentAIModel(model);
    setVisible(undefined);
  };

  return (
    <ModalContainer
      title={t("alertWidgetSelectAIModel.title")}
      onDismiss={() => setVisible(undefined)}
      hideCancel
      modalWidth={Math.min(width, 600)}
      contentStyle={{ marginHorizontal: -ms(24) }}
    >
      <View style={styles.spacer} />

      <ScrollView style={{ maxHeight: height - ms(240) }}>
        {recentAIModels.length > 0 && (
          <View>
            <Text
              style={{
                fontSize: ms(14),
                fontWeight: "300",
                color: Colors.text + "80",
                marginBottom: ms(8),
                marginLeft: ms(24),
              }}
            >
              {t("alertWidgetSelectAIModel.recentlyUsed")}
            </Text>

            {recentAIModels.slice(0, 3).map((model) => (
              <AlertWidgetSelectAIModel.ActionItem
                key={model.id}
                isSelected={model.id === currentAIModel?.id}
                model={model}
                onSelect={() => {
                  useAuth.signIn({
                    callback: () => {
                      onSelectAIModel(model);
                    },
                  });
                }}
              />
            ))}

            <View style={{ height: ms(24) }} />
          </View>
        )}

        <Text
          style={{
            fontSize: ms(14),
            fontWeight: "300",
            color: Colors.text + "80",
            marginBottom: ms(8),
            marginLeft: ms(24),
          }}
        >
          {t("alertWidgetSelectAIModel.more")}
        </Text>

        {aiModels
          .filter((m) => !recentAIModels.some((r) => r.id === m.id))
          .map((model) => (
            <AlertWidgetSelectAIModel.ActionItem
              key={model.id}
              isSelected={model.id === currentAIModel?.id}
              model={model}
              onSelect={() => {
                useAuth.signIn({
                  callback: () => {
                    onSelectAIModel(model);
                  },
                });
              }}
            />
          ))}
      </ScrollView>

      <View style={styles.negativeMargin} />
    </ModalContainer>
  );
};

AlertWidgetSelectAIModel.ActionItem = ({
  model,
  isSelected,
  onSelect,
}: {
  model: AIModel;
  isSelected?: boolean;
  onSelect?: () => void;
}) => {
  const { t } = useTranslation();
  const isSubscribed = useUserSlice((state) => state.isSubscribed);
  return (
    <Pressable
      style={{
        height: ms(56),
        borderBottomWidth: 1,
        borderColor: Colors.text + "10",
        paddingHorizontal: ms(24),
        backgroundColor: isSelected ? Colors.accent + "10" : "transparent",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
      }}
      onPress={onSelect}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: ms(8) }}>
        <Icon
          icon={model.icon as IconType}
          style={{ width: ms(24), height: ms(24) }}
        />
        <Text
          style={{
            fontSize: ms(16),
            color: Colors.text,
            fontWeight: "500",
          }}
        >
          {model.name}
        </Text>
        <Text style={{ fontSize: ms(16), color: Colors.text + "80" }}>
          {model.model}
          {model.isPremium && !isSubscribed && (
            <Text style={{ fontSize: ms(16), color: Colors.text }}>
              {" 🔒"}
            </Text>
          )}
        </Text>
      </View>

      <View style={{ flexDirection: "row", alignItems: "center", gap: ms(12) }}>
        {model.withImage && (
          <Icon
            icon="picture"
            style={{
              tintColor: Colors.accent,
              width: ms(16),
              height: ms(16),
            }}
          />
        )}

        {model.withPDF && (
          <Icon
            icon="document"
            style={{
              tintColor: Colors.accent,
              width: ms(16),
              height: ms(16),
            }}
          />
        )}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  spacer: {
    height: ms(16),
  },
  negativeMargin: {
    marginBottom: -ms(16),
  },
});

export default AlertWidgetSelectAIModel;
